import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
	getAllParentsOptions,
	createParentMutation,
	deleteParentMutation,
	sendPaymentLinkMutation,
	togglePaymentHoldMutation,
	getAllCoursesQueryKey,
	getAllParentsQueryKey,
} from "@/lib/client/@tanstack/react-query.gen";
import type { Parent } from "@/lib/client/types.gen";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
	Plus,
	Trash2,
	Mail,
	Pause,
	Play,
	User,
	Phone,
	MapPin,
	CreditCard,
	Eye,
	Upload,
	X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useForm } from "@tanstack/react-form";
import { z } from "zod";
import { Link, useNavigate } from "@tanstack/react-router";
import {
	getLevelsOptions,
	getSubjectsOptions,
	getTimeSlotsOptions,
	getSchoolsOptions,
} from "@/lib/client/@tanstack/react-query.gen";
import { Checkbox } from "@/components/ui/checkbox";
import type { ColumnDef } from "@tanstack/react-table";
import {
	DataTable,
	DataTableColumnHeader,
	DataTableToolbar,
	DataTableFilterText,
	DataTableFilterFaceted,
} from "@/components/data-table";
import { normalizeString } from "@/lib/utils";

export const Route = createFileRoute("/admin/parents/")({
	component: ParentsManagementPage,
});

const parentSchema = z.object({
	firstName: z.string().min(2, "Le prénom doit contenir au moins 2 caractères"),
	lastName: z.string().min(2, "Le nom doit contenir au moins 2 caractères"),
	email: z.string().email("Email invalide"),
	phone: z.string().min(10, "Numéro de téléphone invalide"),
	address: z.string().min(5, "L'adresse doit être renseignée"),
	paymentMethod: z.enum(["CARD", "SEPA", "BANK_TRANSFER"]),
});

interface StudentFormData {
	id: string;
	firstName: string;
	lastName: string;
	school: string;
	schoolOther?: string;
	level: string;
	className: string;
	subjects: string[];
	timeSlots: string[];
	hasPap: boolean;
	hasPps: boolean;
}

function ParentsManagementPage() {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const [createDialogOpen, setCreateDialogOpen] = useState(false);
	const [students, setStudents] = useState<StudentFormData[]>([]);

	const { data: levels = [] } = useQuery(getLevelsOptions());
	const { data: subjects = [] } = useQuery(getSubjectsOptions());
	const { data: timeSlots = [] } = useQuery(getTimeSlotsOptions());
	const { data: schools = [] } = useQuery(getSchoolsOptions());

	const { data: parents = [], isLoading } = useQuery(getAllParentsOptions());

	const createParentMut = useMutation({
		...createParentMutation(),
		onSuccess: () => {
			toast.success("Parent créé avec succès");
			queryClient.invalidateQueries({ queryKey: getAllParentsQueryKey() });
			setCreateDialogOpen(false);
		},
		onError: () => {
			toast.error("Erreur lors de la création du parent");
		},
	});

	const deleteParentMut = useMutation({
		...deleteParentMutation(),
		onSuccess: () => {
			toast.success("Parent supprimé avec succès");
			queryClient.invalidateQueries({ queryKey: getAllParentsQueryKey() });
		},
		onError: () => {
			toast.error("Erreur lors de la suppression");
		},
	});

	const togglePaymentHoldMut = useMutation({
		...togglePaymentHoldMutation(),
		onSuccess: (_, variables) => {
			toast.success(
				`Paiements ${(variables as any).body?.hold ? "suspendus" : "repris"} avec succès`,
			);
			queryClient.invalidateQueries({ queryKey: getAllParentsQueryKey() });
		},
		onError: () => {
			toast.error("Erreur lors de la modification");
		},
	});

	const form = useForm({
		defaultValues: {
			firstName: "",
			lastName: "",
			email: "",
			phone: "",
			address: "",
			paymentMethod: "CARD" as "CARD" | "SEPA" | "BANK_TRANSFER",
		},
		onSubmit: async ({ value }) => {
			const isValid = parentSchema.safeParse(value);
			if (isValid.success) {
				createParentMut.mutate({
					body: {
						...value,
						students: students.map((s) => ({
							firstName: s.firstName,
							lastName: s.lastName,
							school: s.school as any,
							schoolOther: s.schoolOther,
							level: s.level as any,
							className: s.className,
							subjects: s.subjects as any,
							timeSlots: s.timeSlots as any,
							hasPap: s.hasPap,
							hasPps: s.hasPps,
						})),
					},
				});
			}
		},
	});

	const addStudent = () => {
		setStudents([
			...students,
			{
				id: `student-${Date.now()}`,
				firstName: "",
				lastName: "",
				school: "SAINT_ANDRE",
				level: "SIXIEME",
				className: "",
				subjects: [],
				timeSlots: [],
				hasPap: false,
				hasPps: false,
			},
		]);
	};

	const removeStudent = (id: string) => {
		setStudents(students.filter((s) => s.id !== id));
	};

	const updateStudent = (
		id: string,
		field: keyof StudentFormData,
		value: any,
	) => {
		setStudents(
			students.map((s) => (s.id === id ? { ...s, [field]: value } : s)),
		);
	};

	const toggleStudentSubject = (studentId: string, subject: string) => {
		setStudents(
			students.map((s) => {
				if (s.id === studentId) {
					const subjects = s.subjects.includes(subject)
						? s.subjects.filter((sub) => sub !== subject)
						: [...s.subjects, subject];
					return { ...s, subjects };
				}
				return s;
			}),
		);
	};

	const toggleStudentTimeSlot = (studentId: string, slot: string) => {
		setStudents(
			students.map((s) => {
				if (s.id === studentId) {
					const timeSlots = s.timeSlots.includes(slot)
						? s.timeSlots.filter((ts) => ts !== slot)
						: [...s.timeSlots, slot];
					return { ...s, timeSlots };
				}
				return s;
			}),
		);
	};

	const handleDeleteParent = (parentId: string) => {
		if (confirm("Êtes-vous sûr de vouloir supprimer ce parent ?")) {
			deleteParentMut.mutate({
				path: { id: parentId },
			});
		}
	};

	const handleTogglePaymentHold = (parentId: string, currentHold: boolean) => {
		togglePaymentHoldMut.mutate({
			path: { id: parentId } as any,
			query: {} as any,
			body: { hold: !currentHold } as any,
		} as any);
	};

	const columns = useMemo<ColumnDef<Parent>[]>(
		() => [
			{
				id: "parent",
				accessorFn: (row) => row.lastName + " " + row.firstName,
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Parent" />
				),
				cell: ({ row }) => (
					<div className="flex items-center gap-2">
						<User className="h-4 w-4 text-gray-400" />
						<div>
							<p className="font-medium">
								{row.original.firstName} {row.original.lastName}
							</p>
							<p className="text-sm text-gray-600">
								{row.original.email}
							</p>
						</div>
					</div>
				),
				filterFn: (row, columnId, filterValue: string) => {
					const searchValue = normalizeString(filterValue);
					const fullName = normalizeString(
						`${row.original.firstName} ${row.original.lastName}`
					);
					const reverseName = normalizeString(
						`${row.original.lastName} ${row.original.firstName}`
					);
					const email = normalizeString(row.original.email || "");

					return (
						fullName.includes(searchValue) ||
						reverseName.includes(searchValue) ||
						email.includes(searchValue)
					);
				},
				sortDescFirst: false,
				meta: { label: "Parent" },
			},
			{
				id: "contact",
				accessorFn: (row) => `${row.phone || ""} ${row.address || ""}`,
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Contact" />
				),
				cell: ({ row }) => (
					<div className="space-y-1">
						<div className="flex items-center gap-1 text-sm">
							<Phone className="h-3 w-3" />
							{row.original.phone}
						</div>
						<div className="flex items-center gap-1 text-sm text-gray-600">
							<MapPin className="h-3 w-3" />
							{row.original.address?.substring(0, 30)}...
						</div>
					</div>
				),
				enableSorting: false,
				meta: {
					label: "Contact",
					textValue: (row: Parent) => `${row.phone || ""} - ${row.address || ""}`,
				},
			},
			{
				id: "paymentMethod",
				accessorKey: "paymentMethod",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Paiement" />
				),
				cell: ({ row }) => (
					<div className="space-y-1">
						<div className="flex items-center gap-1">
							<CreditCard className="h-3 w-3" />
							<Badge variant="secondary">
								{row.original.paymentMethod === "CARD" && "Carte"}
								{row.original.paymentMethod === "SEPA" && "SEPA"}
								{row.original.paymentMethod === "BANK_TRANSFER" &&
									"Virement"}
							</Badge>
						</div>
					</div>
				),
				filterFn: (row, id, value: string[]) =>
					value.includes(String(row.getValue(id))),
				meta: {
					label: "Paiement",
					textValue: (row: Parent) => {
						const labels: Record<string, string> = { CARD: "Carte", SEPA: "SEPA", BANK_TRANSFER: "Virement" };
						return labels[row.paymentMethod || ""] || row.paymentMethod || "";
					},
				},
			},
			{
				id: "children",
				accessorFn: (row) => row.students?.length || 0,
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Enfants" />
				),
				cell: ({ row }) => (
					<Badge variant="outline">
						{row.original.students?.length || 0} enfant(s)
					</Badge>
				),
				meta: {
					label: "Enfants",
					textValue: (row: Parent) => {
						const students = row.students || [];
						if (students.length === 0) return "0";
						return students.map((s: any) => `${s.firstName} ${s.lastName}`).join(", ");
					},
				},
			},
			{
				id: "status",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Statut" />
				),
				cell: ({ row }) => (
					<div className="flex items-center gap-2">
						<Badge
							variant={
								row.original.automaticPaymentEnabled
									? "default"
									: "secondary"
							}
						>
							{row.original.automaticPaymentEnabled ? "Actif" : "Inactif"}
						</Badge>
						{row.original.paymentOnHold && (
							<Badge variant="destructive">Suspendu</Badge>
						)}
					</div>
				),
				enableSorting: false,
				meta: { label: "Statut" },
			},
			{
				id: "actions",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Actions" />
				),
				cell: ({ row }) => (
					<div className="flex items-center gap-2">
						<Link
							to="/admin/parents/$id"
							params={{ id: row.original.id || "" }}
						>
							<Button size="sm" variant="outline">
								<Eye className="h-3 w-3" />
							</Button>
						</Link>
						<Button
							size="sm"
							variant="outline"
							onClick={() =>
								handleTogglePaymentHold(
									row.original.id || "",
									row.original.paymentOnHold || false,
								)
							}
							disabled={togglePaymentHoldMut.isPending}
						>
							{row.original.paymentOnHold ? (
								<Play className="h-3 w-3" />
							) : (
								<Pause className="h-3 w-3" />
							)}
						</Button>
						{false && (
							<Button
								size="sm"
								variant="destructive"
								onClick={() =>
									handleDeleteParent(row.original.id || "")
								}
								disabled={deleteParentMut.isPending}
							>
								<Trash2 className="h-3 w-3" />
							</Button>
						)}
					</div>
				),
				enableSorting: false,
				meta: { label: "Actions" },
			},
		],
		[togglePaymentHoldMut.isPending, deleteParentMut.isPending],
	);

	const paymentMethodOptions = [
		{ label: "Carte", value: "CARD" },
		{ label: "SEPA", value: "SEPA" },
		{ label: "Virement", value: "BANK_TRANSFER" },
	];

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
					<p className="mt-4 text-gray-600">Chargement des parents...</p>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex justify-between items-center">
				<div>
					<h1 className="text-3xl font-bold">Gestion des parents</h1>
					<p className="text-gray-600 mt-2">
						Gérez les parents et leurs informations
					</p>
				</div>
				<div className="flex gap-3">
					<Button
						variant="outline"
						onClick={() => navigate({ to: "/admin/parents/import" })}
					>
						<Upload className="mr-2 h-4 w-4" />
						Import en masse
					</Button>
					<Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
						<DialogTrigger asChild>
							<Button>
								<Plus className="mr-2 h-4 w-4" />
								Ajouter un parent
							</Button>
						</DialogTrigger>
						<DialogContent className="max-w-2xl">
							<DialogHeader>
								<DialogTitle>Créer un nouveau parent</DialogTitle>
								<DialogDescription>
									Ajoutez les informations du parent
								</DialogDescription>
							</DialogHeader>
							<form
								onSubmit={(e) => {
									e.preventDefault();
									form.handleSubmit();
								}}
								className="space-y-4"
							>
								<div className="grid grid-cols-2 gap-4">
									<form.Field
										name="firstName"
										children={(field) => (
											<div>
												<Label htmlFor={field.name}>Prénom</Label>
												<Input
													id={field.name}
													value={field.state.value}
													onChange={(e) => field.handleChange(e.target.value)}
												/>
											</div>
										)}
									/>
									<form.Field
										name="lastName"
										children={(field) => (
											<div>
												<Label htmlFor={field.name}>Nom</Label>
												<Input
													id={field.name}
													value={field.state.value}
													onChange={(e) => field.handleChange(e.target.value)}
												/>
											</div>
										)}
									/>
								</div>

								<form.Field
									name="email"
									children={(field) => (
										<div>
											<Label htmlFor={field.name}>Email</Label>
											<Input
												id={field.name}
												type="email"
												value={field.state.value}
												onChange={(e) => field.handleChange(e.target.value)}
											/>
										</div>
									)}
								/>

								<form.Field
									name="phone"
									children={(field) => (
										<div>
											<Label htmlFor={field.name}>Téléphone</Label>
											<Input
												id={field.name}
												value={field.state.value}
												onChange={(e) => field.handleChange(e.target.value)}
											/>
										</div>
									)}
								/>

								<form.Field
									name="address"
									children={(field) => (
										<div>
											<Label htmlFor={field.name}>Adresse</Label>
											<Input
												id={field.name}
												value={field.state.value}
												onChange={(e) => field.handleChange(e.target.value)}
												placeholder="123 Rue de la Paix, Paris"
											/>
										</div>
									)}
								/>

								<form.Field
									name="paymentMethod"
									children={(field) => (
										<div>
											<Label htmlFor={field.name}>Moyen de paiement</Label>
											<Select
												value={field.state.value}
												onValueChange={(value) =>
													field.handleChange(
														value as "CARD" | "SEPA" | "BANK_TRANSFER",
													)
												}
											>
												<SelectTrigger>
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="CARD">Carte bancaire</SelectItem>
													<SelectItem value="SEPA">Prélèvement SEPA</SelectItem>
													<SelectItem value="BANK_TRANSFER">
														Virement bancaire
													</SelectItem>
												</SelectContent>
											</Select>
										</div>
									)}
								/>

								<div className="space-y-4">
									<div className="flex justify-between items-center">
										<Label>Enfants</Label>
										<Button
											type="button"
											variant="outline"
											size="sm"
											onClick={addStudent}
										>
											<Plus className="h-4 w-4 mr-2" />
											Ajouter un enfant
										</Button>
									</div>

									{students.map((student) => (
										<Card key={student.id} className="p-4">
											<div className="space-y-3">
												<div className="flex justify-between items-center">
													<h4 className="font-semibold text-sm">Enfant</h4>
													<Button
														type="button"
														variant="ghost"
														size="sm"
														onClick={() => removeStudent(student.id)}
													>
														<X className="h-4 w-4 text-red-500" />
													</Button>
												</div>

												<div className="grid grid-cols-2 gap-3">
													<div>
														<Label>Prénom</Label>
														<Input
															value={student.firstName}
															onChange={(e) =>
																updateStudent(
																	student.id,
																	"firstName",
																	e.target.value,
																)
															}
															placeholder="Prénom"
														/>
													</div>
													<div>
														<Label>Nom</Label>
														<Input
															value={student.lastName}
															onChange={(e) =>
																updateStudent(
																	student.id,
																	"lastName",
																	e.target.value,
																)
															}
															placeholder="Nom"
														/>
													</div>
												</div>

												<div className="grid grid-cols-2 gap-3">
													<div>
														<Label>Établissement</Label>
														<Select
															value={
																student.school?.startsWith("AUTRE")
																	? "AUTRE"
																	: student.school
															}
															onValueChange={(value) => {
																if (value === "AUTRE") {
																	updateStudent(
																		student.id,
																		"school",
																		"AUTRE ()",
																	);
																} else {
																	updateStudent(student.id, "school", value);
																}
															}}
														>
															<SelectTrigger>
																<SelectValue />
															</SelectTrigger>
															<SelectContent>
																{schools.map((school) => (
																	<SelectItem
																		key={school.value}
																		value={school.value}
																	>
																		{school.label}
																	</SelectItem>
																))}
															</SelectContent>
														</Select>
														{student.school?.startsWith("AUTRE") && (
															<Input
																className="mt-2"
																placeholder="Nom de l'établissement"
																value={
																	student.school.match(
																		/AUTRE\s*\((.*?)\)/,
																	)?.[1] || ""
																}
																onChange={(e) =>
																	updateStudent(
																		student.id,
																		"school",
																		`AUTRE (${e.target.value})`,
																	)
																}
															/>
														)}
													</div>
													<div>
														<Label>Niveau</Label>
														<Select
															value={student.level}
															onValueChange={(value) =>
																updateStudent(student.id, "level", value)
															}
														>
															<SelectTrigger>
																<SelectValue />
															</SelectTrigger>
															<SelectContent>
																{levels.map((level) => (
																	<SelectItem
																		key={level.value}
																		value={level.value}
																	>
																		{level.label}
																	</SelectItem>
																))}
															</SelectContent>
														</Select>
													</div>
												</div>

												<div>
													<Label>Classe</Label>
													<Input
														value={student.className}
														onChange={(e) =>
															updateStudent(
																student.id,
																"className",
																e.target.value,
															)
														}
														placeholder="Ex: 6ème A, CM2 B..."
													/>
												</div>

												<div>
													<Label>Matières</Label>
													<div className="flex flex-wrap gap-2 mt-2">
														{subjects.map((subject) => (
															<Badge
																key={subject.value}
																variant={
																	student.subjects.includes(subject.value)
																		? "default"
																		: "outline"
																}
																className="cursor-pointer"
																onClick={() =>
																	toggleStudentSubject(
																		student.id,
																		subject.value,
																	)
																}
															>
																{subject.label}
															</Badge>
														))}
													</div>
												</div>

												<div>
													<Label>Créneaux</Label>
													<div className="flex flex-wrap gap-2 mt-2">
														{timeSlots.map((slot) => (
															<Badge
																key={slot.value}
																variant={
																	student.timeSlots.includes(slot.value)
																		? "default"
																		: "outline"
																}
																className="cursor-pointer"
																onClick={() =>
																	toggleStudentTimeSlot(student.id, slot.value)
																}
															>
																{slot.label}
															</Badge>
														))}
													</div>
												</div>

												<div className="flex gap-4">
													<div className="flex items-center space-x-2">
														<Checkbox
															id={`pap-${student.id}`}
															checked={student.hasPap}
															onCheckedChange={(checked) =>
																updateStudent(student.id, "hasPap", checked)
															}
														/>
														<label
															htmlFor={`pap-${student.id}`}
															className="text-sm cursor-pointer"
														>
															PAP
														</label>
													</div>
													<div className="flex items-center space-x-2">
														<Checkbox
															id={`pps-${student.id}`}
															checked={student.hasPps}
															onCheckedChange={(checked) =>
																updateStudent(student.id, "hasPps", checked)
															}
														/>
														<label
															htmlFor={`pps-${student.id}`}
															className="text-sm cursor-pointer"
														>
															PPS
														</label>
													</div>
												</div>
											</div>
										</Card>
									))}
								</div>

								<DialogFooter>
									<Button
										variant="outline"
										type="button"
										onClick={() => setCreateDialogOpen(false)}
									>
										Annuler
									</Button>
									<Button type="submit" disabled={createParentMut.isPending}>
										{createParentMut.isPending ? "Création..." : "Créer"}
									</Button>
								</DialogFooter>
							</form>
						</DialogContent>
					</Dialog>
				</div>
			</div>

			<Card>
				<CardHeader>
					<CardTitle>Liste des parents ({parents.length})</CardTitle>
					<CardDescription>
						Tous les parents inscrits dans le système
					</CardDescription>
				</CardHeader>
				<CardContent>
					<DataTable
						columns={columns}
						data={parents || []}
						emptyMessage="Aucun parent enregistré"
						toolbar={(table) => (
							<DataTableToolbar table={table} showExportExcel exportFilename="parents">
								{table.getColumn("parent") && (
									<DataTableFilterText
										column={table.getColumn("parent")!}
										placeholder="Rechercher un parent..."
									/>
								)}
								{table.getColumn("paymentMethod") && (
									<DataTableFilterFaceted
										column={table.getColumn("paymentMethod")!}
										title="Paiement"
										options={paymentMethodOptions}
									/>
								)}
							</DataTableToolbar>
						)}
					/>
				</CardContent>
			</Card>
		</div>
	);
}
